import type { ActivityRewardItem } from '@/features/activity/activity-api';
import { oroMxSkinRoot } from '@/shared/theme/oro-mx-home-data';

const skin = oroMxSkinRoot;

export const oroMxSignSourceAssets = {
  chest: `${skin}/signin/chest.png`,
  chestClaimed: `${skin}/signin/chest-claimed.png`,
  chestUnfinished: `${skin}/signin/chest-unfinished.png`,
  freebet: `${skin}/signin/freebet.png`,
  goldCoin: `${skin}/signin/gold-coins.png`,
  header: `${skin}/signin/hero.png`,
  reward: `${skin}/signin/reward.png`,
  tab7day: `${skin}/signin/tab-7day.png`,
  tabTask: `${skin}/signin/tab-task.png`,
  wheel: `${skin}/signin/wheel.png`,
  wufu: `${skin}/signin/wufu.png`,
} as const;

const CODE_IMAGE: Record<string, string> = {
  FREEBET: oroMxSignSourceAssets.freebet,
  REWARD: oroMxSignSourceAssets.reward,
  WHEEL: oroMxSignSourceAssets.wheel,
  WUFU: oroMxSignSourceAssets.wufu,
};

export type OroMxSignClaimReward = {
  image: string;
  key: string;
  primary: string;
  secondary: string;
};

export function oroMxSignRewardImage(code?: string) {
  const key = String(code ?? '').trim().toUpperCase();
  return CODE_IMAGE[key] || oroMxSignSourceAssets.chest;
}

/** 源站 Vs()：已领 claimed / 可领 chest / 其余 unfinished。 */
export function oroMxSignChestImage(stage: { actionKind?: string; finished?: boolean }) {
  if (stage.finished) {
    return oroMxSignSourceAssets.chestClaimed;
  }
  if (stage.actionKind === 'claim') {
    return oroMxSignSourceAssets.chest;
  }
  return oroMxSignSourceAssets.chestUnfinished;
}

/** 源站 xs()：金额文案前加 x。入参是分。 */
export function oroMxSignClaimCoinLabel(minor: number) {
  const major = Math.max(0, minor) / 100;
  const value = Number.isInteger(major) ? String(major) : major.toFixed(2);
  return value.startsWith('x') ? value : `x${value}`;
}

export function oroMxSignClaimRewardsFromMinor(minor: number): OroMxSignClaimReward[] {
  if (!(minor > 0)) {
    return [
      {
        image: oroMxSignSourceAssets.chest,
        key: 'default',
        primary: '',
        secondary: '',
      },
    ];
  }
  return [
    {
      image: oroMxSignSourceAssets.goldCoin,
      key: 'coin',
      primary: oroMxSignClaimCoinLabel(minor),
      secondary: '',
    },
  ];
}

export type OroMxSignRewardCopy = {
  coupon: (percent: number) => string;
  scratch: string;
  wheel: string;
};

/** 后台库存奖 → 源站预览/领取弹层行。空列表留给弹层显示 no_data。 */
export function oroMxSignRewardsFromItems(
  items: ActivityRewardItem[] | undefined,
  copy: OroMxSignRewardCopy,
): OroMxSignClaimReward[] {
  if (!items?.length) {
    return [];
  }
  return items.map((item, index) => {
    const qty = Math.max(0, item.qty || 0);
    const key = `${item.itemType || 'item'}-${index}`;
    if (item.itemType === 'DEPOSIT_COUPON') {
      const percent = Math.max(0, Math.round((item.couponPercentBps ?? 0) / 100));
      return {
        image: oroMxSignSourceAssets.reward,
        key,
        primary: copy.coupon(percent),
        secondary: '',
      };
    }
    if (item.itemType === 'WHEEL_SPIN') {
      return {
        image: oroMxSignSourceAssets.wheel,
        key,
        primary: copy.wheel,
        secondary: qty > 1 ? oroMxSignClaimCoinLabel(qty * 100) : '',
      };
    }
    if (item.itemType === 'SCRATCH_CHANCE') {
      return {
        image: oroMxSignSourceAssets.wufu,
        key,
        primary: copy.scratch,
        secondary: qty > 0 ? oroMxSignClaimCoinLabel(qty * 100) : '',
      };
    }
    return {
      image: oroMxSignSourceAssets.chest,
      key,
      primary: '',
      secondary: '',
    };
  });
}

export function oroMxSignClaimRewards(
  items: ActivityRewardItem[] | undefined,
  minor: number,
  copy: OroMxSignRewardCopy,
): OroMxSignClaimReward[] {
  const fromConfig = oroMxSignRewardsFromItems(items, copy);
  return fromConfig.length ? fromConfig : oroMxSignClaimRewardsFromMinor(minor);
}
